// Centralized date formatting. Pattern is "DD MMM, YYYY" for dates and
// "DD MMM, YYYY, HH:MM AM/PM" for datetimes — matches the convention used
// across the app. Day/month/year order is enforced via formatToParts so
// it's independent of the user's region; time formatting follows the
// browser locale's AM/PM convention.

const DATE_PARTS_FMT = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

function partsToDate(d: Date): string {
  const parts = DATE_PARTS_FMT.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  // "24 Apr, 2026"
  return `${get("day")} ${get("month")}, ${get("year")}`;
}

// Parse a backend datetime string to an absolute instant. FastAPI
// serializes naive UTC datetimes with no timezone designator (e.g.
// "2026-07-13T07:41:00"), which `new Date()` would read as *local* time —
// displaying the raw UTC clock value instead of converting it to the
// viewer's zone. Status-history rows carry a trailing `Z`, but the
// evaluation/verification timestamps don't, so the same action rendered
// ~5.5h apart across components. Treat any designator-less *datetime* as
// UTC so every surface converts identically. Date-only strings
// ("2026-04-24") are left as-is (already UTC-midnight by spec).
function parseInstant(iso: string): Date {
  const s = iso.trim();
  const isDateTime = /\d[T ]\d/.test(s);
  const hasDesignator = /(?:Z|[+-]\d{2}:?\d{2})$/.test(s);
  if (isDateTime && !hasDesignator) {
    return new Date(`${s.replace(" ", "T")}Z`);
  }
  return new Date(s);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = parseInstant(iso);
    return `${partsToDate(d)}, ${TIME_FMT.format(d)}`;
  } catch {
    return iso;
  }
}

// Legacy WC/SC evaluation history rows stored the raw outcome value
// ("not_recommend", "in_process") in their synthetic remark, so the
// timeline showed underscores. The backend now writes the human label
// at the source; this display-side rewrite covers the already-written
// rows. Only the outcome token inside the known evaluation-remark shape
// is touched — a lowercase snake_case word can't appear there any other
// way — so user-typed remarks pass through verbatim.
const EVALUATION_REMARK_OUTCOME = /(evaluation recorded — )([a-z]+(?:_[a-z]+)*)( · total )/;

// The Steering Committee was renamed SSC -> SC. Rows written before that
// keep "SSC" in their stored remark, so the committee token is rewritten —
// but only inside the remark shapes the backend itself writes (the
// evaluation remark and the transitions' version-history labels).
const LEGACY_SC_REMARKS: ReadonlyArray<[RegExp, string]> = [
  [/^SSC(?= evaluation recorded\b)/, "SC"],
  [/\b(Accepted by|Approved by|Rejected by|forwarded to) SSC\b/g, "$1 SC"],
];

export function formatHistoryRemark(remark: string): string {
  const renamed = LEGACY_SC_REMARKS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    remark,
  );
  return renamed.replace(EVALUATION_REMARK_OUTCOME, (_m, pre, outcome, post) => {
    const label = outcome
      .split("_")
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    return `${pre}${label}${post}`;
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return partsToDate(parseInstant(iso));
  } catch {
    return iso;
  }
}

// Compact relative age for log rows — "just now", "4m", "3h", "2d",
// then an absolute date once the relative form stops being useful.
const RELATIVE_STEPS: ReadonlyArray<[seconds: number, suffix: string]> = [
  [60, "s"],
  [3600, "m"],
  [86400, "h"],
];

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const then = parseInstant(iso);
    const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
    if (!Number.isFinite(seconds)) return formatDate(iso);
    if (seconds < 45) return "just now";
    for (let i = 0; i < RELATIVE_STEPS.length; i++) {
      const [limit, suffix] = RELATIVE_STEPS[i];
      if (seconds < limit) {
        const divisor = i === 0 ? 1 : RELATIVE_STEPS[i - 1][0];
        return `${Math.floor(seconds / divisor)}${suffix}`;
      }
    }
    const days = Math.floor(seconds / 86400);
    // Past a week the day count stops carrying information — show the
    // date instead.
    return days <= 7 ? `${days}d` : partsToDate(then);
  } catch {
    return formatDate(iso);
  }
}
